// Returns the date in MM/dd/yyyy format
export function getDateInMMddyyyyFormat(dateTime: string): string {
  if (dateTime === null) {
    return '';
  }
  const date = new Date(dateTime);
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  const year = String(date.getFullYear());

  if (month.length < 2) {
    month = '0' + month;
  }
  if (day.length < 2) {
    day = '0' + day;
  }

  return `${month}/${day}/${year}`;
}

/**
 * Converts a UTC ISO 8601 timestamp string to the user's local browser timezone.
 * Returns formatted string: "yyyy-MM-dd HH:mm:ss (TZ)"
 *
 * @param utcDateStr - UTC date string (e.g. "2026-05-18T14:30:00Z" or "2026-05-18T14:30:00")
 * @returns Formatted local date string with timezone abbreviation, or empty string if invalid
 */
export function convertUtcToLocalDisplay(utcDateStr: string | null | undefined): string {
  if (!utcDateStr || utcDateStr.trim() === '') {
    return '';
  }

  // Ensure the string is treated as UTC if no timezone indicator present
  let normalizedStr = utcDateStr.trim();
  if (!normalizedStr.endsWith('Z') && !normalizedStr.includes('+') && !/\d{2}:\d{2}$/.test(normalizedStr.slice(-6))) {
    normalizedStr += 'Z';
  }

  const date = new Date(normalizedStr);
  if (isNaN(date.getTime())) {
    return utcDateStr; // Return original if parsing fails
  }

  const pad = (n: number): string => (n < 10 ? '0' + n : String(n));

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // Get timezone abbreviation using Intl API
  const tzAbbr = getTimezoneAbbreviation(date);

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} (${tzAbbr})`;
}

/**
 * Gets the timezone abbreviation for a given date (handles DST automatically).
 * Example: "EDT", "PST", "IST", "CET"
 */
export function getTimezoneAbbreviation(date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value || Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback: calculate UTC offset
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const h = Math.floor(absOffset / 60);
    const m = absOffset % 60;
    return `UTC${sign}${h}${m > 0 ? ':' + (m < 10 ? '0' + m : m) : ''}`;
  }
}
